import React, { useState, useEffect } from 'react';
import { AlertCircle, Check, Loader, Mail, Save, X, Send, MessageSquare } from 'lucide-react';
import { api } from '../services/api';
import { AdminLoading } from './admin/AdminLoading';
import { AdminPageHeader } from './admin/AdminPageHeader';

const DEFAULT_TEMPLATE = `New customer message received

Customer: {customerFirstName} {customerLastName}
Phone: {customerPhone}
Message: {message}
Time: {time}
Store: {storeName}

View this conversation: {leadLink}`;

const PLACEHOLDERS = [
  'customerFirstName',
  'customerLastName',
  'customerPhone',
  'customerEmail',
  'message',
  'time',
  'storeName',
  'leadId',
  'leadLink'
];

const ReceivedMessageConfigManagement = () => {
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [config, setConfig] = useState(null);
  const [telegramConfigs, setTelegramConfigs] = useState([]);
  const [slackConfigs, setSlackConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    messageTemplate: DEFAULT_TEMPLATE,
    telegramConfigId: '',
    telegramEnabled: false,
    slackConfigId: '',
    slackEnabled: false
  });

  useEffect(() => {
    loadStores();
  }, []);

  useEffect(() => {
    if (selectedStore) {
      loadConfig(selectedStore.id);
    }
  }, [selectedStore]);

  const loadStores = async () => {
    setError('');
    try {
      const response = await api.get('/admin/stores');
      setStores(response.data);
      if (response.data.length > 0) {
        setSelectedStore(response.data[0]);
      }
    } catch (err) {
      console.error('Error loading stores:', err);
      setError('Failed to load stores');
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async (storeId) => {
    setError('');
    setSuccess('');
    setConfig(null);
    setTelegramConfigs([]);
    setSlackConfigs([]);
    setShowForm(false);
    setLoading(true);
    try {
      const [templateRes, telegramListRes, slackListRes] = await Promise.allSettled([
        api.get('/admin/received-message-config', { params: { storeIdFk: storeId } }),
        api.get('/admin/telegram-config/list', { params: { storeIdFk: storeId } }),
        api.get('/admin/slack-config/list', { params: { storeIdFk: storeId } })
      ]);

      const template = templateRes.status === 'fulfilled' ? templateRes.value.data : null;
      const telegramList = telegramListRes.status === 'fulfilled' ? telegramListRes.value.data : [];
      const slackList = slackListRes.status === 'fulfilled' ? slackListRes.value.data : [];

      const selectedTelegram = telegramList.find((c) => c.id === template?.storeTelegramConfigId) || null;
      const selectedSlack = slackList.find((c) => c.id === template?.storeSlackConfigId) || null;

      setConfig(template);
      setTelegramConfigs(telegramList);
      setSlackConfigs(slackList);
      setFormData({
        messageTemplate: template?.messageTemplate || DEFAULT_TEMPLATE,
        telegramConfigId: template?.storeTelegramConfigId || '',
        telegramEnabled: template?.telegramEnabled || false,
        slackConfigId: template?.storeSlackConfigId || '',
        slackEnabled: template?.slackEnabled || false
      });

      if (templateRes.status === 'rejected' && templateRes.reason?.response?.status !== 404) {
        console.error('Error loading received message config:', templateRes.reason);
        setError('Failed to load configuration');
      }
      if (telegramListRes.status === 'rejected') {
        console.error('Error loading Telegram configs:', telegramListRes.reason);
      }
      if (slackListRes.status === 'rejected') {
        console.error('Error loading Slack configs:', slackListRes.reason);
      }
    } catch (err) {
      console.error('Error loading config:', err);
      setError('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selectedStore) return;

    if (!formData.messageTemplate.trim()) {
      setError('Message template is required');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    const templatePayload = {
      storeIdFk: selectedStore.id,
      messageTemplate: formData.messageTemplate,
      storeTelegramConfigId: formData.telegramConfigId ? parseInt(formData.telegramConfigId, 10) : null,
      storeSlackConfigId: formData.slackConfigId ? parseInt(formData.slackConfigId, 10) : null,
      telegramEnabled: formData.telegramEnabled,
      slackEnabled: formData.slackEnabled
    };

    try {
      const res = await api.post('/admin/received-message-config', templatePayload);
      setConfig(res.data);
      setSuccess('Configuration saved successfully');
      setShowForm(false);
    } catch (err) {
      console.error('Error saving received message config:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const selectedTelegram = telegramConfigs.find((c) => c.id === config?.storeTelegramConfigId);
  const selectedSlack = slackConfigs.find((c) => c.id === config?.storeSlackConfigId);

  const startEditing = () => {
    setFormData({
      messageTemplate: config?.messageTemplate || DEFAULT_TEMPLATE,
      telegramConfigId: config?.storeTelegramConfigId || '',
      telegramEnabled: config?.telegramEnabled || false,
      slackConfigId: config?.storeSlackConfigId || '',
      slackEnabled: config?.slackEnabled || false
    });
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const copyPlaceholder = (placeholder) => {
    const text = `{${placeholder}}`;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text)
        .catch((err) => console.error('Failed to copy placeholder:', err));
    }
  };

  if (loading) return <AdminLoading message="Loading received message notification configuration..." />;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        icon={Mail}
        title="Received Message Notification Configuration"
        subtitle="Configure the template and the Telegram/Slack channels used for customer message notifications"
        rightContent={
          <select
            value={selectedStore?.id || ''}
            onChange={(e) => {
              const store = stores.find((s) => s.id === parseInt(e.target.value));
              setSelectedStore(store);
            }}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            {stores.map((store) => (
              <option key={store.id} value={store.id}>
                {store.name || store.storeId}
              </option>
            ))}
          </select>
        }
      />

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="rounded-md bg-green-50 p-4">
          <div className="flex">
            <Check className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm text-green-800">{success}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-blue-50 rounded-md p-4">
        <div className="flex items-center justify-between mb-2">
          <h5 className="text-sm font-medium text-blue-900">Available placeholders</h5>
          <span className="text-xs text-blue-700">Click to copy</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {PLACEHOLDERS.map((placeholder) => (
            <button
              key={placeholder}
              type="button"
              onClick={() => copyPlaceholder(placeholder)}
              className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-blue-800 bg-white border border-blue-200 rounded hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <code className="font-mono">{`{${placeholder}}`}</code>
            </button>
          ))}
        </div>
      </div>

      {!showForm && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Current Configuration</h3>
              <p className="text-sm text-gray-500">Store: {selectedStore?.name || selectedStore?.storeId}</p>
            </div>
            <button
              onClick={startEditing}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <Mail className="h-4 w-4 mr-2" />
              Edit Configuration
            </button>
          </div>

          <div className="bg-white shadow rounded-lg border-l-4 border-blue-500 p-6 space-y-4">
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Message Template</h4>
              <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md whitespace-pre-wrap min-h-[100px]">
                {config?.messageTemplate || DEFAULT_TEMPLATE}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border-l-4 border-blue-300 pl-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 flex items-center">
                  <Send className="h-3 w-3 mr-1" />
                  Telegram {selectedTelegram?.name ? `(${selectedTelegram.name})` : ''}
                </h4>
                <p className="text-sm text-gray-900">
                  <span className="font-medium">Notifications enabled:</span> {config?.telegramEnabled ? 'Yes' : 'No'}
                </p>
                <p className="text-sm text-gray-900">
                  <span className="font-medium">Chat ID:</span> {selectedTelegram?.chatId || 'Not set'}
                </p>
                <p className="text-sm text-gray-900">
                  <span className="font-medium">Token:</span> {selectedTelegram?.hasToken ? 'Configured' : 'Not set'}
                </p>
              </div>
              <div className="border-l-4 border-purple-300 pl-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 flex items-center">
                  <MessageSquare className="h-3 w-3 mr-1" />
                  Slack {selectedSlack?.name ? `(${selectedSlack.name})` : ''}
                </h4>
                <p className="text-sm text-gray-900">
                  <span className="font-medium">Notifications enabled:</span> {config?.slackEnabled ? 'Yes' : 'No'}
                </p>
                <p className="text-sm text-gray-900">
                  <span className="font-medium">Channel:</span> {selectedSlack?.channel || 'Not set'}
                </p>
                <p className="text-sm text-gray-900">
                  <span className="font-medium">Token:</span> {selectedSlack?.hasBotToken ? 'Configured' : 'Not set'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSave} className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              {config?.id ? 'Edit Configuration' : 'Create Configuration'}
            </h3>
            <button
              type="button"
              onClick={() => { setShowForm(false); setError(''); }}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="bg-white shadow rounded-lg border-l-4 border-blue-500 p-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Message Template</label>
              <textarea
                value={formData.messageTemplate}
                onChange={(e) => setFormData({ ...formData, messageTemplate: e.target.value })}
                rows={10}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm px-3 py-2"
                required
              />
              <p className="mt-1 text-xs text-gray-500">Use the placeholders above to customize the notification.</p>
            </div>
          </div>

          <ChannelFormSection
            title="Telegram"
            icon={Send}
            borderColor="blue"
            configs={telegramConfigs}
            configId={formData.telegramConfigId}
            chatIdLabel="Chat ID"
            enabled={formData.telegramEnabled}
            onConfigChange={(value) => setFormData({ ...formData, telegramConfigId: value })}
            onChange={(_, value) => setFormData({ ...formData, telegramEnabled: value })}
          />

          <ChannelFormSection
            title="Slack"
            icon={MessageSquare}
            borderColor="purple"
            configs={slackConfigs}
            configId={formData.slackConfigId}
            chatIdLabel="Channel ID"
            enabled={formData.slackEnabled}
            onConfigChange={(value) => setFormData({ ...formData, slackConfigId: value })}
            onChange={(_, value) => setFormData({ ...formData, slackEnabled: value })}
          />

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => { setShowForm(false); setError(''); }}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <X className="h-4 w-4 inline mr-1" />
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-70 flex items-center"
            >
              {saving ? (
                <>
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

const ChannelFormSection = ({
  title,
  icon: Icon,
  borderColor,
  configs,
  configId,
  chatIdLabel,
  enabled,
  onConfigChange,
  onChange
}) => {
  const borderClass = borderColor === 'purple' ? 'border-purple-500' : 'border-blue-500';
  const textClass = borderColor === 'purple' ? 'text-purple-500' : 'text-blue-500';
  return (
    <div className={`bg-white shadow rounded-lg border-l-4 ${borderClass} p-6`}>
      <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
        <Icon className={`h-5 w-5 mr-2 ${textClass}`} />
        {title}
      </h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">{title} channel</label>
          <select
            value={configId}
            onChange={(e) => onConfigChange(e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm px-3 py-2"
          >
            <option value="">None</option>
            {configs.map((c) => (
              <option key={`${title}-${c.id}`} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        {configId && (
          <div className="bg-gray-50 p-3 rounded-md space-y-3">
            {(() => {
              const selectedConfig = configs.find((c) => c.id === parseInt(configId, 10));
              const recipientValue = title === 'Telegram' ? selectedConfig?.chatId : selectedConfig?.channel;
              return (
                <div className="space-y-3">
                  <div className="text-sm text-gray-700 space-y-1">
                    <p><span className="font-medium">Name:</span> {selectedConfig?.name || 'Not set'}</p>
                    <p><span className="font-medium">{chatIdLabel}:</span> {recipientValue || 'Not set'}</p>
                    <p><span className="font-medium">Config enabled:</span> {selectedConfig?.enabled ? 'Yes' : 'No'}</p>
                  </div>
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id={`${title}-enabled`}
                        type="checkbox"
                        checked={enabled}
                        onChange={(e) => onChange('enabled', e.target.checked)}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor={`${title}-enabled`} className="font-medium text-gray-700">Enable {title} notifications for this store</label>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReceivedMessageConfigManagement;
